import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, Text } from '@Sage/hoi-poi-ui';

import { SALESORDERS } from 'constants/Entities';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';

import './styles.scss';

/** Wide layout (min-width: 1200px in styles.scss). Kept for callers that need a static default. */
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT = 94;
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT = 210;
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM = 156;

/** Max width for the compact (tallest) layout - keep in sync with styles.scss. */
const SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT = 879;
/** Min width for the wide (shortest) layout - keep in sync with styles.scss. */
const SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE = 1200;

function getSalesOrdersBottomInfoPanelHeight(viewportWidth) {
    if (viewportWidth <= SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT;
    }
    if (viewportWidth < SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM;
    }
    return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT;
}

export function useSalesOrdersBottomInfoPanelHeight() {
    // --- State
    const [height, setHeight] = useState(() =>
        typeof window !== 'undefined'
            ? getSalesOrdersBottomInfoPanelHeight(window.innerWidth)
            : SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT,
    );

    // --- Effects
    useEffect(() => {
        const onResize = () => {
            setHeight(getSalesOrdersBottomInfoPanelHeight(window.innerWidth));
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return height;
}

const ITEM_TYPE_FIELD = 'field';
const ITEM_TYPE_SEPARATOR = 'separator';
const ITEM_TYPE_HORIZONTAL_RULE = 'horizontalRule';
const ITEM_TYPE_STACKED_TOTALS = 'stackedTotals';
const ITEM_MODE_HORIZONTAL = 'horizontal';
const ITEM_MODE_VERTICAL = 'vertical';

const PLACEHOLDER_VALUE = '-';

const SalesOrdersBottomInfoPanel = memo(({ data, showTaxDetails, showShippingCost }) => {
    // --- Context & hooks
    const theme = useTheme();

    // --- Derived state
    const idEnvironment = useMemo(() => data?.idBranch?.value ?? data?.idBranch, [data?.idBranch]);
    const idCurrency = useMemo(
        () => data?.idCurrency?.value ?? data?.idCurrency,
        [data?.idCurrency],
    );

    // --- Handlers
    const formatAmount = useCallback(
        (value, fieldId) => {
            return value != null
                ? formatDecimalByConfig({
                      value,
                      type: DECIMAL_TYPES.CURRENCY_GENERAL,
                      idEnvironment,
                      idCurrency,
                      entityName: SALESORDERS.extraFieldName,
                      fieldId,
                  })
                : PLACEHOLDER_VALUE;
        },
        [idEnvironment, idCurrency],
    );

    // --- Derived state
    const legacyRecalculatedTotals = useMemo(() => {
        if (showTaxDetails || !data?.products?.length) return null;
        const manager = Context.entityManager.getEntitiesManager(SALESORDERS);
        if (!manager) return null;
        return manager.calculateEntityProducts(
            {
                products: data.products,
                discount1: data.discount1,
                discount2: data.discount2,
                discount3: data.discount3,
                discount4: data.discount4,
                currencySymbol: data.currencySymbol,
                idBranch: data.idBranch,
                idCurrency: data.idCurrency,
                shippingCostRaw: data.shippingCostRaw ?? data.shippingCost ?? null,
            },
            'edit',
        );
    }, [
        showTaxDetails,
        data?.products,
        data?.discount1,
        data?.discount2,
        data?.discount3,
        data?.discount4,
        data?.currencySymbol,
        data?.idBranch,
        data?.idCurrency,
        data?.shippingCostRaw,
        data?.shippingCost,
    ]);

    const items = useMemo(() => {
        const result = [];
        const subtotalValue = showTaxDetails
            ? data?.subtotalAmount
            : (legacyRecalculatedTotals?.subtotalAmount ?? data?.subtotalAmount);
        const discountTotalValue = showTaxDetails
            ? data?.discountTotalAmount
            : (legacyRecalculatedTotals?.discountTotalAmount ?? data?.discountTotalAmount);
        // Legacy document total (`totalAmount` / amountSalesOrder). Prefer `totalRaw`
        // (kept in sync while editing) over the initial backend snapshot.
        const legacyTotalValue = data?.totalRaw ?? data?.amountSalesOrder;
        const finalTotalValue = showTaxDetails ? data?.finalTotalAmount : legacyTotalValue;
        const finalTotalFieldId = showTaxDetails ? 'finalTotalAmount' : 'totalAmount';

        result.push(
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_subtotal'),
                value: formatAmount(subtotalValue, 'subtotalAmount'),
                mode: ITEM_MODE_VERTICAL,
            },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_discounts'),
                value: formatAmount(discountTotalValue, 'discountTotalAmount'),
                mode: ITEM_MODE_VERTICAL,
            },
        );

        if (showTaxDetails) {
            result.push(
                {
                    type: ITEM_TYPE_FIELD,
                    label: getLiteral('label_salesorder_header_tax_base'),
                    value: formatAmount(data?.taxableBaseAmount, 'taxableBaseAmount'),
                    mode: ITEM_MODE_VERTICAL,
                },
                { type: ITEM_TYPE_HORIZONTAL_RULE },
                {
                    type: ITEM_TYPE_FIELD,
                    label: getLiteral('label_salesorder_header_total_vat'),
                    value: formatAmount(data?.taxTotalAmount, 'taxTotalAmount'),
                    mode: ITEM_MODE_VERTICAL,
                },
            );
        }

        result.push({ type: ITEM_TYPE_SEPARATOR });

        if (showShippingCost) {
            result.push({
                type: ITEM_TYPE_STACKED_TOTALS,
                items: [
                    {
                        label: getLiteral('label_salesorder_header_total_including_taxes'),
                        value: formatAmount(finalTotalValue, finalTotalFieldId),
                        isTotal: true,
                        valueType: 'subtitle1',
                        mode: ITEM_MODE_HORIZONTAL,
                    },
                    {
                        label: getLiteral('label_salesorder_header_shipping_cost'),
                        value: formatAmount(data?.shippingCost, 'shippingCost'),
                        labelType: 'badges',
                        valueType: 'badges',
                        mode: ITEM_MODE_HORIZONTAL,
                    },
                ],
            });
        } else {
            result.push({
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_including_taxes'),
                value: formatAmount(finalTotalValue, finalTotalFieldId),
                isTotal: true,
                mode: ITEM_MODE_HORIZONTAL,
            });
        }

        return result;
    }, [
        formatAmount,
        showTaxDetails,
        showShippingCost,
        legacyRecalculatedTotals,
        data?.subtotalAmount,
        data?.discountTotalAmount,
        data?.taxableBaseAmount,
        data?.taxTotalAmount,
        data?.shippingCost,
        data?.finalTotalAmount,
        data?.amountSalesOrder,
        data?.totalRaw,
    ]);

    // --- Render
    return (
        <div className="sales-orders-bottom-info-panel">
            {items.map((item, index) => {
                const itemModeClass =
                    item.mode === ITEM_MODE_HORIZONTAL
                        ? 'sales-orders-bottom-info-panel__item--horizontal'
                        : '';

                switch (item.type) {
                    case ITEM_TYPE_HORIZONTAL_RULE:
                        return (
                            <div
                                key={`${ITEM_TYPE_HORIZONTAL_RULE}-${index}`}
                                className="sales-orders-bottom-info-panel__horizontal-rule"
                            />
                        );
                    case ITEM_TYPE_SEPARATOR:
                        return (
                            <div
                                key={`${ITEM_TYPE_SEPARATOR}-${index}`}
                                className="sales-orders-bottom-info-panel__separator"
                            />
                        );
                    case ITEM_TYPE_STACKED_TOTALS:
                        return (
                            <div
                                key={`${ITEM_TYPE_STACKED_TOTALS}-${index}`}
                                className="sales-orders-bottom-info-panel__item"
                            >
                                <div className="sales-orders-bottom-info-panel__stacked-totals">
                                    {item.items.map((stackedItem) => (
                                        <div
                                            key={stackedItem.label}
                                            className={`sales-orders-bottom-info-panel__stacked-total ${
                                                stackedItem.mode === ITEM_MODE_HORIZONTAL
                                                    ? 'sales-orders-bottom-info-panel__stacked-total--horizontal'
                                                    : ''
                                            }`}
                                        >
                                            <Text
                                                type={stackedItem.labelType ?? 'caption'}
                                                color={theme.colors.utility.textSecondary}
                                                className="sales-orders-bottom-info-panel__label"
                                            >
                                                {stackedItem.label}
                                            </Text>
                                            <Text
                                                type={stackedItem.valueType}
                                                medium
                                                className="sales-orders-bottom-info-panel__value"
                                            >
                                                {stackedItem.value}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    default:
                        return (
                            <div
                                key={item.label}
                                className={`sales-orders-bottom-info-panel__item ${itemModeClass}`}
                            >
                                <Text
                                    type={item.labelType ?? 'caption'}
                                    color={theme.colors.utility.textSecondary}
                                    className="sales-orders-bottom-info-panel__label"
                                >
                                    {item.label}
                                </Text>
                                <Text
                                    type={item.valueType ?? (item.isTotal ? 'subtitle1' : 'body')}
                                    medium
                                    className="sales-orders-bottom-info-panel__value"
                                >
                                    {item.value}
                                </Text>
                            </div>
                        );
                }
            })}
        </div>
    );
});

SalesOrdersBottomInfoPanel.displayName = 'SalesOrdersBottomInfoPanel';

export default SalesOrdersBottomInfoPanel;
